// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import Router from 'next/router';
import { useQuery } from '@tanstack/react-query';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCTextArea from '@/components/sc-controls/form-controls/sc-textarea';
import SCCheckbox from '@/components/sc-controls/form-controls/sc-checkbox';
import Button from '@/components/button';
import Breadcrumbs from '@/components/breadcrumbs';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import ToastContext from '@/utils/toast-context';
import * as Enums from '@/utils/enums';
import SettingsMenu from '@/components/settings/menu';
import Helper from '@/utils/helper';
import ConfirmAction from '@/components/modals/confirm-action';
import OptionService from '@/services/option/option-service';
import CompanyService from '@/services/company-service';
import Storage from '@/utils/storage';
import SCNumericInput from '@/components/sc-controls/form-controls/sc-numeric-input';
import Time from '@/utils/time';
import Constants from "@/utils/constants";
import RecordSequenceSettings from '@/PageComponents/Settings/RecordSequence/RecordSequenceSettings';
import RecordSequenceHeader from '@/PageComponents/Settings/RecordSequence/RecordSequenceHeader';
import RecordSequenceHint from '@/PageComponents/Settings/RecordSequence/RecordSequenceHint';
import { Box, Flex, Space } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router'




function QuoteSettings() {

  const toast = useContext(ToastContext);
  let currPath = "";
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState<any>(null);

  const [company, setCompany] = useState<any>(null);

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  
  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  };

  useEffect(() => {
    getAccessStatus();
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace,"/");
    }
  }, [accessStatus]);

  const { data: settingsData } = useQuery(
    ['quoteSettings'],
    () => OptionService.getCustomFields(Enums.Module.Quote)
  );

  const { data: companyData } = useQuery(
    ['company'],
    () => CompanyService.getCompany()
  );

  useEffect(() => {
    if (settingsData && !settings) {
      const enriched = settingsData.findIndex((x: any) => x.OptionName === 'Quote Default Deposit Percentage') !== -1
        ? settingsData
        : [...settingsData, {
            IsActive: true,
            CreatedBy: 'Client',
            CreatedDate: Time.now(),
            ModifiedBy: 'Client',
            ModifiedDate: Time.now(),
            Module: Enums.Module.Quote,
            GroupName: 'Quote Settings',
            OptionName: 'Quote Default Deposit Percentage',
            OptionValue: Constants.defaultQuoteDepositPercentage + '',
            Description: null,
            SystemType: 'System.Decimal',
            Unit: '%',
            IsEditable: true,
            IsRequired: true,
          }];
      setSettings(enriched);
      setInputs(OptionService.getSettingInputs(enriched));
    }
  }, [settingsData]);

  useEffect(() => {
    if (companyData && !company) {
      setCompany(companyData);
    }
  }, [companyData]);

  const [inputs, setInputs] = useState<any>({});
  const [inputErrors, setInputErrors] = useState<any>({});

  const handleInputChange = (e: any) => {
    setInputs({
      ...inputs,
      [e.name]: e.value
    });
    setFormIsDirty(true);
  };

  const handleCompanyInputChange = (e: any) => {
    setCompany({...company, 
      [e.name]: e.value
    });
    setFormIsDirty(true);
  }

  let settingGroups = OptionService.getSettingGroups(settings);

  const groupNames = Object.keys(settingGroups).filter((item: any) => item !== 'Prefix Settings');

  const validate = () => {
    let validationItems = [
      { key: 'QuoteExpiryPeriod', value: company.QuoteExpiryPeriod, gte: 0, type: Enums.ControlType.Number },
    ];
    const { isValid, errors } = Helper.validateInputs(validationItems);
    setInputErrors(errors);
    return isValid;
  };

  async function saveSettings() {
    setSubmitting(true);

    let isValid = validate();

    if (isValid) {
      let newSettings = [...settings];
      newSettings.map(function (setting: any) {
        setting.OptionValue = inputs[setting.OptionName]?.toString() ?? null;
      });
  
      const settingsRes = await OptionService.saveOption(newSettings, toast);
      const companySaveRes = await CompanyService.saveCompany(company, toast);

      if (settingsRes.ResponseStatus == 200 && companySaveRes.Name) {
        const rowUpdate = await OptionService.getCustomFields(Enums.Module.Quote);
        setSettings(rowUpdate);
        setCompany(companySaveRes);

        toast.setToast!!!({
          message: 'Quote settings updated successfully',
          show: true,
          type: Enums.ToastType.success
        });
      }
    } else {
      toast.setToast!!!({
        message: 'There are errors on the page',
        show: true,
        type: Enums.ToastType.error
      });
    }
    
    setSubmitting(false);
    setFormIsDirty(false);

    return isValid;
  }

  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, saveSettings);

    const getSettingDetails = (groupName: any) => {
        let group = settingGroups[groupName];
        let results = group.map((setting: any, index: any) => {
            switch (setting.SystemType) {
            case 'System.Boolean':
                return () => <SCCheckbox key={index}
                name={setting.OptionName}
                onChange={() => handleInputChange({ name: setting.OptionName, value: !inputs[setting.OptionName] })}
                value={inputs[setting.OptionName]}
                label={setting.OptionName}
                />
            case 'System.String':
                return () =>
                <SCInput key={index}
                    onChange={handleInputChange}
                    error={inputErrors[setting.OptionName]}
                    label={setting.OptionName}
                    name={setting.OptionName}
                    hint={setting.Description}
                    required={setting.IsRequired}
                    value={inputs[setting.OptionName]}
                />
            case 'System.Decimal':
                return () =>
                <SCNumericInput key={index}
                    format={Enums.NumericFormat.Decimal}
                    onChange={(x: any) => handleInputChange({...x, value: x.value + ''})} // value must be a string
                    error={inputErrors[setting.OptionName]}
                    label={setting.OptionName}
                    name={setting.OptionName}
                    hint={setting.Description}
                    required={setting.IsRequired}
                    max={100}
                    min={0}
                    step={5}
                    value={+inputs[setting.OptionName]}
                />
            default:
                return null;
            }
        });

        let rows = [];
        let counter = 0;
        for (let index = 0; index < Math.ceil(results.length / 2); index++) {  
            rows.push(() => {return (            
                <div className="row">
                    <div className="column"> {results[counter++]()}</div>
                    {results.length > counter ? <div className="column"> {results[counter++]()} </div> : <></>}
                    <style jsx>{`
                        .row {
                            display: flex;
                        }
                        .column {
                            display: flex;
                            flex-direction: column;
                            width: ${layout.inputWidth};
                        }
                        .column + .column {
                            margin-left: 1.25rem;
                        }
                    `}</style>
                </div>)});
        }

        return rows;
    };

  if (!settings || !company) return null;

  return (
    <div className="settings-container">
      {/*<div className="row">
        <div className="title">
          <Breadcrumbs
            crumbs={[
              { text: 'Settings', link: '/settings', suppressLink: true },
              { text: 'Quote', link: '/settings/quote/manage' },
            ]}
          />
        </div>
      </div>*/}
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <div className="settings-card">
          <div className="row">
            <div className="column">
              <h2>Quote Settings</h2>
            </div>
            <div className="column-end">
              <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                text={submitting ? "Saving" : "Save"} extraClasses="auto save" onClick={saveSettings} />
            </div>
          </div>
          <div className="row">
            <div className="column">
              <SCNumericInput
                  name="QuoteExpiryPeriod"
                  label="Quote expiry period"
                  onChange={handleCompanyInputChange}
                  format={Enums.NumericFormat.Integer}
                  value={company.QuoteExpiryPeriod}
                  error={inputErrors.QuoteExpiryPeriod}
                  cypress="data-cy-quoteexpiry"
                />
            </div>            
          </div>
          <div className="row">
            <div className="column">
              <SCTextArea
                  name="QuoteComment"
                  label="Quote note"
                  onChange={handleCompanyInputChange}
                  value={company.QuoteComment}
                  cypress="data-cy-quotecomment"
                />
            </div>
          </div>

          {groupNames.map(function (groupName: any, index: any) {
            return (
              <div key={index}>
                <div className="section-title">
                  {groupName}
                </div>
                {getSettingDetails(groupName).map((item: any) => {
                    return item();
                })}
              </div>
            )
          })}

          <Box mt="xl">
            <RecordSequenceHeader />
          </Box>
          <Flex gap="lg" mt="md">
            <Box style={{ flex: 1 }}>
              <RecordSequenceSettings module={Enums.Module.Quote} />
            </Box>
            <Box style={{ flex: 1 }}>
              <RecordSequenceHint />
            </Box>
          </Flex>
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />
      <Space h={80} />

      <style jsx>{`
        .row {
            display: flex;
            justify-content: space-between;
        }
        .row-no-justify {
            display: flex;
        }
        .column {
          display: flex;
          flex-direction: column;
          width: ${layout.inputWidth};
        }
        .column-equal {
          flex: 1;
        }
        .column-end {
          align-items: flex-end;
        }
        .column-margin {
          margin-left: 24px;
        }
        
        .title {
          color: ${colors.darkPrimary};
          position: relative;
          width: 100%;
        }
        .title h1 {
          font-size: 24px;
          margin: 6px 0 2rem 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }
        .section-title {
          background-color: ${colors.background};
          border-radius: ${layout.cardRadius};
          box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
          color: ${colors.bluePrimary};
          font-size: 1.25rem;
          font-weight: bold;
          margin-top: 2rem;
          opacity: 0.9;
          padding: 1rem;
        }
        .small-title {
          color: ${colors.white};
          font-size: 0.875rem;
          font-weight: bold;
          margin-top: 2rem;
        }
        .container :global(.input-container) {
          width: 50%;
        }
        .title :global(.save) {
          margin: 0;
          position: absolute;
          right: 0;
          top: 0;
        }
      `}</style>
    </div>
  );
}

export default QuoteSettings;

export const Route = createFileRoute('/_authenticated/settings/quote/manage')({ component: QuoteSettings })
